import { ClassNames, css } from '@emotion/core'
import * as React from 'react'

import { breakpoints } from '../constants/breakPoints'
import { DEFAULT_HUBSPOT_PORTAL_ID } from '../constants/hubspot'
import { useHsFormCallback } from '../hooks/useHsFormCallback'
import { getAnonymousId, getUtmParameter } from '../hooks/useTracking'
import { HubSpotFormApi, HubspotFormProps } from '../interfaces/hubspot'
import { NoSSR } from './NoSSR'

export const HubspotForm: React.FunctionComponent<HubspotFormProps> = ({
  consent,
  hubspotPortalId = DEFAULT_HUBSPOT_PORTAL_ID,
  hubspotFormId,
  isCentered = false,
  redirect,
  submittedBlock,
  withGrayBackground = false,
  halfScreenWidth = false,
  fullWidthButton = false,
  blackBG = false,
  submitOnRight = false,
  buttonType = 'primary',
  withWhiteBackground = false,
  cssStyles,
}) => {
  const [formSubmitted, setFormSubmitted] = React.useState(false)

  useHsFormCallback({
    onFormSubmittedCallback: () => setFormSubmitted(true),
    hubspotFormIds: [hubspotFormId ? hubspotFormId : ''],
  })

  return submittedBlock && formSubmitted ? (
    <>{submittedBlock}</>
  ) : (
    <React.Fragment>
      <ClassNames>
        {(emotion) => (
          <NoSSR>
            <Form
              hubspotFormId={hubspotFormId}
              hubspotPortalId={hubspotPortalId}
              redirect={redirect}
              cssClass={emotion.css`
                ${
                  formStyle(
                    withGrayBackground,
                    blackBG,
                    halfScreenWidth,
                    submitOnRight,
                    isCentered,
                    buttonType,
                    withWhiteBackground
                  ) + cssStyles
                }
              `}
              submitButtonClass={emotion.css`
                ${submitButtonStyle(fullWidthButton, isCentered, buttonType)}
              `}
            />
            {consent && <div css={consentStyle}>{consent}</div>}
          </NoSSR>
        )}
      </ClassNames>
    </React.Fragment>
  )
}

const Form: React.FunctionComponent<{
  submitButtonClass?: string
  cssClass?: string
  hubspotFormId: string
  hubspotPortalId?: string
  redirect?: string
}> = ({ submitButtonClass, hubspotPortalId = DEFAULT_HUBSPOT_PORTAL_ID, hubspotFormId, cssClass, redirect }) => {
  const id = React.useMemo(() => '_' + Date.now() + Math.random().toString(36).substring(2, 9), [])

  const createForm = (hubspot: HubSpotFormApi) => {
    hubspot.forms.create({
      portalId: hubspotPortalId,
      formId: hubspotFormId,
      target: `#${id}`,
      redirectUrl: redirect,
      submitButtonClass,
      cssClass,
      onReady: (form: HTMLFormElement) => {
        // Legal consent validation
        form.removeAttribute('novalidate')
        form.querySelector('input[name="LEGAL_CONSENT.subscription_type_3001955"]')?.setAttribute('required', '')

        // UTM hidden fields filling
        const anonymous_id = getAnonymousId()
        const utm = getUtmParameter()

        Object.entries({ anonymous_id, ...utm }).forEach(([key, value]) => {
          const input: HTMLInputElement | null = form.querySelector(`input[name=${key}]`)
          if (input && value) {
            input.value = value
          }
        })
      },
    })
  }

  React.useEffect(() => {
    const handleScriptLoad = () => {
      if (window.hbspt) {
        createForm(window.hbspt as HubSpotFormApi)
        return
      }
    }

    // Check if the script is already loaded and available
    if (window.hbspt) {
      handleScriptLoad()
      // Exit early
      return
    }

    const scriptSrc = '//js.hsforms.net/forms/v2.js'
    const createScript = () => {
      const script = document.createElement('script')
      script.src = scriptSrc
      script.async = true
      script.defer = true
      document.head.appendChild(script)
      return script
    }

    // Find the script if it exists, or create it if it doesn't
    const script: HTMLScriptElement = document.querySelector(`script[src*="${scriptSrc}"]`) ?? createScript()

    // Add the event listener
    script.addEventListener('load', handleScriptLoad)

    // Cleanup function
    return () => {
      script.removeEventListener('load', handleScriptLoad)
    }
  }, [])

  return <div id={id} css={formContainerStyle} />
}

const formStyle = (
  withGrayBackground?: boolean,
  blackBG?: boolean,
  halfScreenWidth?: boolean,
  submitOnRight?: boolean,
  isCentered?: boolean,
  buttonType?: string,
  withWhiteBackground?: boolean
) => `
  margin: 2rem ${isCentered ? ' auto' : '0'} 4rem;
  display: grid;
  grid-gap: 1rem;
  position: relative;
  max-width: 31.25rem;
  border-radius: 0.5rem;

  .is-placeholder {
      color: #777;
    }

  ${
    withGrayBackground &&
    `
    background: #F7F7F8;
    padding: 2rem;
  `
  };


  ${
    submitOnRight &&
    `
    grid-template-areas:
        'a b'
        'c c';
    grid-template-columns: auto min-content;
    max-width: 35rem;
  `
  };

  .hs_email,
  .hs-phone {
    .hs-error-msgs {
      display: block !important;
      .hs-error-msg {
        color: red !important;
      }

      li:nth-child(1) {
        margin-bottom: 0;
      }

      li:nth-child(2) {
        display: none;
      }
    }

    ${
      submitOnRight &&
      `
      grid-area: a;
      ${!isCentered && 'max-width: 27rem;'};
      @media screen and (max-width: ${breakpoints.medium}px) {
        ${isCentered && 'margin: 0 auto; width: 100%;'};
      }
    `
    };
  }

  .hs-form-field:last-child {
    .input {
      margin-right: 0 !important;
    }
  }

  .hs_submit {
    ${submitOnRight && 'grid-area: b;'};
  }

  ${
    submitOnRight &&
    `@media screen and (max-width: ${breakpoints.small}px) {
        grid-template-areas: 'a' 'b' 'c';
        grid-template-columns: 1fr;
    }`
  };

  fieldset.form-columns-2 .input {
    @media screen and (max-width: ${breakpoints.small}px) {
      .hs-input {
        width: 100% !important;
      }
    }

    @media screen and (max-width: 480px) {
      margin-right: 0 !important;
    }
  }

  fieldset.form-columns-1 .input {
    margin-right: 0 !important;
    .hs-input[type='text'], .hs-input[type='email'], .hs-input[type='tel'] {
      width: 100% !important;
    }

    select {
      width: 100% !important;
    }

    .hs-fieldtype-intl-phone > select {
      width: 30% !important;
    }
  }

  .hs-fieldtype-intl-phone {
    width: 100% !important;

    &:active,
    &:focus
    {
      padding: 0 !important;
      border: none !important;
    }
  }

  fieldset.form-columns-1 .hs-fieldtype-intl-phone.hs-input .hs-input[type='tel'] {
    width: 68% !important;
    float: right;
  }

  .hs-form-booleancheckbox-display {
    display: flex;

    span {
      margin-left: 0.25rem !important;
    }
  }

  .hs-input {
    padding: 0.75rem 1rem;
    ${!submitOnRight && 'margin-top: 0.5rem;'};
    outline: none;
    height: 3rem;
    border: 1px solid #efeff0;
    box-sizing: border-box;
    border-radius: 0.25rem;
    ${halfScreenWidth ? 'width: 50%' : 'width: 100%'};
    caret-color: #318EFF;
    text-align: left !important;
    &:active,
    &:focus
    {
      padding: calc(0.75rem - 1px) calc(1rem - 1px);
      border: 2px solid #318EFF;
    }
    &:disabled {
      color: #DFE0E3;
      background: #fff;
    }

    &:placeholder {
      color: #88929E;
    }

    ${blackBG && 'color: #000'};
  }

  @-moz-document url-prefix() {
    .input > select {
      padding: 0.75rem;
      &:active,
      &:focus {
        padding: calc(0.75rem - 1px);
      }
    }
  }

  textarea {
      resize: vertical;
      min-height: 4rem !important;
      width: 100% !important;
  }

  [type='checkbox'] {
    border-radius: 0.25rem;

    &::before {
      border-radius: 0.25rem;
    }

    &:checked:before {
      background-image: url('/images/white-checkmark.svg');
    }
  }

  [type='radio'] {
    border-radius: 50%;

    &::before {
      border-radius: 50%;
    }
  }

  [type='checkbox'],
  [type='radio'],
  [type='text'],
  [type='email'],
  [type='number'],
  [type='date'],
  [type='textarea'],
  [type='tel']
  {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
  }

  [type='checkbox'],
  [type='radio']
  {
    width: 20px !important;
    height: 20px;
    padding: 0 !important;

    margin-right: 1rem;
    margin-top: 0.3rem;
    position: relative;

    &:hover:before,
    &:focus:before,
    &:checked:before {
      border: 3px solid #318EFF !important;
      height: 15px !important;
      width: 15px !important;
    }

    &:focus {
      border: none !important;
    }

    &::before {
      background-color: #fff;
      background-position: center center;
      background-repeat: no-repeat;
      background-size: 70%;
      border: 1px solid #88929E;
      content: "";
      display: block;
      height: 18px;
      width: 18px;
      position: absolute;
      left: 0;
      top: 0;
      pointer-events: none;
      user-select: none;
    }

    &:checked:before {
        background-color: #318EFF;
    }
  }

  .hs-error-msgs {
    display: none !important;
  }

  label {
    color: ${blackBG ? '#fff' : '#44505F'};
    margin-bottom: 0.5rem;
  }

  select, input, label, textarea, .legal-consent-container {
    font-size: 0.875rem;
    font-weight: 400;
  }

  .legal-consent-container {
    color: ${blackBG ? '#fff' : '#2E384D'};
    ${submitOnRight && 'grid-area: c;'};
  }

   & > .legal-consent-container {
    ${isCentered && '& > div > p { text-align: center; }'};
   }

  .legal-consent-container > div > p > a {
    ${buttonType === 'primary' && 'color: #318eff !important'};
  }

  select {
    -moz-appearance: none; /* Firefox */
    -webkit-appearance: none; /* Safari and Chrome */
    appearance: none;
    background-image: url('/images/chevron.svg');
    background-repeat: no-repeat;
    background-position-y: 50%;
    background-position-x: calc(100% - 1rem);
    padding-right: 2rem !important;
    background-color: #fff;

    &:hover {
      cursor: pointer;
    }

    &:focus {
      background-position-x: calc(100% - 1rem + 1px);
    }
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    select, input, text-area {
      font-size: 100% !important;
    }
  }

  .hs-fieldtype-intl-phone {
    margin-top: 0;
  }

  ${
    withWhiteBackground &&
    `
    padding: 2.75rem 2rem;
    box-shadow: 10px 10px 50px 20px rgba(49, 142, 255, 0.1);
  `
  };
`

const consentStyle = css`
  margin-top: 0.5rem;
  font-size: 0.7rem;
  color: #a2b2bf;

  @media screen and (max-width: ${breakpoints.medium}px) {
    text-align: center;
  }
`

const submitButtonStyle = (fullWidthButton: boolean, isCentered: boolean, buttonType?: string) => `
  -webkit-appearance: none;
  display: inline-block;
  text-decoration: none;
  text-align: center;
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-weight: 600 !important;
  font-size: 1rem !important;
  height: 3rem;
  letter-spacing: 0.13px;
  cursor: pointer;
  box-sizing: border-box;
  outline: none;
  transition: all 0.5s ease;
  user-select: none;
  ${buttonType === 'primary' && 'background: #318eff'};
  ${
    isCentered &&
    `
    display: block;
    margin: 0 auto;
  `
  };
  ${fullWidthButton && 'width: 100%;'};
  &:hover {
    ${buttonType === 'primary' && 'background: #3146ff'};
  }
`

const formContainerStyle = css`
  .submitted-message {
    font-family:
      'Inter',
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      Roboto,
      Oxygen-Sans,
      Ubuntu,
      Cantarell,
      'Helvetica Neue',
      sans-serif;
    font-size: 1.125rem !important;
    font-weight: 400;
    line-height: 1.625rem !important;
    margin: 1.5rem 0 0.5rem;
    font-weight: bold;
  }
`
